import React from 'react';

import { styled, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import TickOutlinedAnimatedIcon from '~/assets/icons/TickOutlinedAnimatedIcon';

type ParticleType = {
  color?: 0 | 1 | 2 | 3;
  mirrorPathX?: boolean;
  rotatePathDeg?: number;
  scalePath?: number;
};

const particles: ParticleType[] = [
  {
    color: 3,
    rotatePathDeg: 1,
    scalePath: 1.03,
  },
  {
    color: 1,
    rotatePathDeg: 5,
    scalePath: 0.85,
  },
  {
    rotatePathDeg: 29,
    scalePath: 0.8,
  },
  {
    color: 3,
    rotatePathDeg: 45,
    scalePath: 0.84,
  },
  {
    color: 2,
    rotatePathDeg: 60,
    scalePath: 0.9,
  },
  {
    color: 1,
    rotatePathDeg: 75,
    scalePath: 0.96,
  },
  {
    rotatePathDeg: 80,
    scalePath: 0.93,
  },
  {
    color: 1,
    mirrorPathX: true,
    rotatePathDeg: 181,
    scalePath: 0.83,
  },
  {
    color: 1,
    rotatePathDeg: 96,
    scalePath: 0.98,
  },
  {
    color: 3,
    rotatePathDeg: 107,
    scalePath: 0.94,
  },
  {
    color: 1,
    rotatePathDeg: 120,
    scalePath: 0.81,
  },
  {
    color: 3,
    rotatePathDeg: 134,
    scalePath: 0.88,
  },
  {
    mirrorPathX: true,
    rotatePathDeg: 120,
    scalePath: 0.86,
  },
  {
    color: 3,
    mirrorPathX: true,
    rotatePathDeg: 110,
    scalePath: 0.92,
  },
  {
    rotatePathDeg: 186,
    scalePath: 0.98,
  },
  {
    color: 1,
    mirrorPathX: true,
    rotatePathDeg: 83,
    scalePath: 0.98,
  },
  {
    color: 3,
    mirrorPathX: true,
    rotatePathDeg: 66,
    scalePath: 0.93,
  },
  {
    mirrorPathX: true,
    rotatePathDeg: 40,
    scalePath: 0.85,
  },
  {
    color: 3,
    mirrorPathX: true,
    rotatePathDeg: 29,
    scalePath: 0.89,
  },
  {
    color: 1,
    mirrorPathX: true,
    rotatePathDeg: 6,
    scalePath: 0.87,
  },
  {
    mirrorPathX: true,
    rotatePathDeg: 2,
    scalePath: 1.07,
  },
  {
    color: 2,
    mirrorPathX: true,
    rotatePathDeg: -9,
  },
  {
    rotatePathDeg: -10,
  },
  {
    color: 3,
    rotatePathDeg: -28,
    scalePath: 0.81,
  },
];

const PathFollowingParticle = ({ color, mirrorPathX, rotatePathDeg, scalePath }: ParticleType) => {
  const theme = useTheme();
  const colors = [
    theme.palette.common.blue,
    alpha(theme.palette.common.blue, 0.33),
    alpha(theme.palette.common.blue, 0.15),
    theme.palette.primary.light,
  ];
  const fill = colors[color || 0];
  const compensatedParticleScale = 1 / (scalePath || 1);

  return (
    <g transform={`scale(${scalePath || 1})`}>
      <g transform={`scale(${mirrorPathX ? -1 : 1}, 1) rotate(${rotatePathDeg || 0})`}>
        <ParticleWrapper transform={`scale(${compensatedParticleScale})`}>
          <Particle r={2} fill={fill} />
        </ParticleWrapper>
      </g>
    </g>
  );
};

const Confetti = () => {
  return (
    <svg width="110" height="110" viewBox="-55 -55 110 110">
      <path d="M0,0 C7,-35 20,-58 40,-42" fill="none" id="pathForParticle" />
      {particles.map((particle, i) => (
        <PathFollowingParticle {...particle} key={i} />
      ))}
    </svg>
  );
};

interface TickOutlinedAnimatedIconWithConfettiProps {
  'data-testid'?: string;
}

const TickOutlinedAnimatedIconWithConfetti = (props: TickOutlinedAnimatedIconWithConfettiProps) => (
  <Wrapper data-testid={props['data-testid']}>
    <Confetti />
    <StyledTickOutlinedAnimatedIcon width="62" height="62" />
  </Wrapper>
);

export default TickOutlinedAnimatedIconWithConfetti;

const Wrapper = styled('span')`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StyledTickOutlinedAnimatedIcon = styled(TickOutlinedAnimatedIcon)`
  color: ${({ theme }) => theme.palette.common.accentFaded};
  position: absolute;
`;
const ParticleWrapper = styled('g')`
  offset-path: url('#pathForParticle');
  offset-distance: 38%;
  animation: bezier-path 2.5s 1.3s cubic-bezier(0.16, 0.81, 0.48, 0.78);

  @keyframes bezier-path {
    to {
      offset-distance: 100%;
    }
  }
`;
const Particle = styled('circle')`
  animation: particle-scale 2.5s 1.3s linear, particle-opacity 2.5s 1.3s linear;
  opacity: 0;

  @keyframes particle-scale {
    20% {
      scale: 1;
    }

    to {
      scale: 0.6;
    }
  }

  @keyframes particle-opacity {
    0% {
      opacity: 1;
    }

    50% {
      opacity: 0.9;
    }

    100% {
      opacity: 0;
    }
  }
`;
